import { computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useAuthStore } from "@/stores/auth";
import { useAppPage } from "@/composables/useAppPage";
import { formatarParaCarrossel, formatarParaMenuLista } from "@/helpers/formatter";
import { useProtocoloNavigation } from "@/composables/navigation/useProtocoloNavigation";
import { useAtendimentoNavigation } from "@/composables/navigation/useAtendimentoNavigation";
import { useTributosNavigation } from "@/composables/navigation/useTributosNavigation";
import { useCertidaoNavigation } from "@/composables/navigation/useCertidaoNavigation";
import { useServidorNavigation } from "@/composables/navigation/useServidorNavigation";
import { useContribuinteNavigation } from "@/composables/navigation/useContribuinteNavigation";
import { useFornecedorNavigation } from "@/composables/navigation/useFornecedorNavigation";
import { useMotoristaNavigation } from "@/composables/navigation/useMotoristaNavigation";
import { useServicosNavigation } from "@/composables/navigation/useServicosNavigation";
import { useImoveisNavigation } from "@/composables/navigation/useImoveisNavigation";

export const useItensMenuStore = defineStore('itensMenu', () => {
    // AUTH
    const authStore = useAuthStore();

    const { isAuthenticated, personas } = storeToRefs(authStore);

    // CONFIGURAÇÃO ITENS DE MENU
    const { menusAreasVisiveis } = useAppPage();

    // NAVEGAÇÃO
    const {
        goToConsultaProtocolo,
        goToMeusProtocolos
    } = useProtocoloNavigation();

    const { goToConsultaAtendimento } = useAtendimentoNavigation();

    const { goToTributos } = useTributosNavigation();

    const { goToEmissaoCertidoes } = useCertidaoNavigation();

    const {
        goToAreaServidor,
        getAssentamentosRoute,
        getAverbacoesRoute,
        getFeriasRoute,
        getComprovanteRendimentosRoute,
        getContrachequesRoute
    } = useServidorNavigation();

    const { goToAreaContribuinte } = useContribuinteNavigation();

    const { goToAreaFornecedor } = useFornecedorNavigation();

    const { goToAreaMotorista } = useMotoristaNavigation();

    const { goToServicos } = useServicosNavigation();

    const { goToImoveis } = useImoveisNavigation();

    function hasPersona(persona) {
        if (!personas.value) return false;

        return personas.value.some(p => p.persona.toUpperCase() === persona);
    }

    const menusVisiveis = computed(() => {
        const items = [];

        if (!isAuthenticated.value) {
            items.push(formatarParaCarrossel(
                'Consultar Atendimento',
                'fa-solid fa-comments',
                () => goToConsultaAtendimento(),
            ));
        }

        items.push(formatarParaCarrossel(
            'Consultar Protocolo',
            'fa-solid fa-folder-open',
            () => goToConsultaProtocolo()
        ));

        if (menusAreasVisiveis.value.menu_consulta_debitos) {
            items.push(formatarParaCarrossel(
                'Tributos',
                'fa-solid fa-file-invoice-dollar',
                () => goToTributos()
            ));
        }

        items.push(formatarParaCarrossel(
            'Imóveis',
            'fa-solid fa-house',
            () => goToImoveis()
        ));

        if (isAuthenticated.value) {
            items.push(formatarParaCarrossel(
                'Meus Atendimentos',
                'fa-solid fa-comments',
                () => goToConsultaAtendimento()
            ));

            items.push(formatarParaCarrossel(
                'Meus Protocolos',
                'fa-solid fa-box-archive',
                () => goToMeusProtocolos()
            ));
        }

        if (menusAreasVisiveis.value.menu_area_certidoes) {
            items.push(formatarParaCarrossel(
                'Emissão de Certidões',
                'fa-solid fa-scroll',
                () => goToEmissaoCertidoes()
            ));
        }

        if (
            hasPersona('SERVIDOR') &&
            menusAreasVisiveis.value.menu_area_servidor &&
            isAuthenticated.value
        ) {
            items.push(formatarParaCarrossel(
                'Área do Servidor',
                'fa-solid fa-id-card-clip',
                () => goToAreaServidor()
            ));
        }

        if (
            hasPersona('CONTRIBUINTE IMOBILIARIO') &&
            menusAreasVisiveis.value.menu_area_contribuinte &&
            isAuthenticated.value
        ) {
            items.push(formatarParaCarrossel(
                'Área do Contribuinte',
                'fa-solid fa-dollar-sign',
                () => goToAreaContribuinte()
            ));
        }

        if (
            hasPersona('FORNECEDOR') &&
            menusAreasVisiveis.value.menu_area_fornecedor &&
            isAuthenticated.value
        ) {
            items.push(formatarParaCarrossel(
                'Área do Fornecedor',
                'fa-solid fa-cart-shopping',
                () => goToAreaFornecedor()
            ));
        }

        if (
            hasPersona('MOTORISTA') &&
            menusAreasVisiveis.value.menu_area_motorista &&
            isAuthenticated.value
        ) {
            items.push(formatarParaCarrossel(
                'Área do Motorista',
                'fa-solid fa-car',
                () => goToAreaMotorista()
            ));
        }

        items.push(formatarParaCarrossel(
            'Serviços',
            'fa-solid fa-screwdriver-wrench',
            () => goToServicos('instituicoes')
        ));

        return items;
    });

    function getItensMenuServidor(matricula) {
        const items = [
            formatarParaMenuLista(
                1,
                'Assentamentos',
                'fa-solid fa-bars-staggered',
                getAssentamentosRoute(matricula)
            ),
            formatarParaMenuLista(
                2,
                'Averbações',
                'fa-solid fa-file-pen',
                getAverbacoesRoute(matricula)
            ),
            formatarParaMenuLista(
                3,
                'Férias',
                'fa-solid fa-umbrella-beach',
                getFeriasRoute(matricula)
            ),
            formatarParaMenuLista(
                4,
                'Comprovante de Rendimentos',
                'fa-solid fa-file-invoice-dollar',
                getComprovanteRendimentosRoute(matricula)
            ),
            formatarParaMenuLista(
                5,
                'Contracheques',
                'fa-solid fa-receipt',
                getContrachequesRoute(matricula)
            )
        ];

        if (process.env.MIX_DESABILITA_ASSENTAMENTOS === 'true') {
            return items.filter(item => item.codigo !== 1);
        }

        if (process.env.MIX_DESABILITA_COMPROVANTE_RENDIMENTOS === 'true') {
            return items.filter(item => item.codigo !== 4);
        }

        return items;
    }

    return {
        menusVisiveis,
        getItensMenuServidor
    };
});
